import React, { useContext } from "react";
import { GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { PricingI } from "../../../../../../api/request/Lead/Interfaces/PricingInterface";
import { Lead } from "../../../../../../api/request/Lead/Interfaces/DataInterface.response";
import numberFormat from "../../../../../../utils/Utils";
import { priceUpdatingText } from "../pricing/dashboardPrice";
import { cardStyle } from "../cardStyles";
import { PendingCapitalI } from "../../../../../../api/request/Lead/Interfaces/PolicyInterface";
import PendingCapitalLine from "../pendingCapital/PendingCapitalLine";

interface HeroSectionProps {
  lead?: Lead;
  isCancelled: boolean;
  capital: number;
  pendingCapital?: PendingCapitalI | null;
  frequency: string;
  nextPaymentDate?: string;
  hasPromo: boolean;
  /** null = sin prima (cohorte SCOR FR): se pinta el texto neutro, nunca un importe. */
  regularPrice: number | null;
  displayPrice: number | null;
  pricing?: PricingI;
  onShowConditions: () => void;
}

const formatPaymentDate = (dateStr?: string) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
  });
};

const HeroSection = ({
  lead,
  isCancelled,
  capital,
  pendingCapital,
  frequency,
  nextPaymentDate,
  hasPromo,
  regularPrice,
  displayPrice,
  pricing,
  onShowConditions,
}: HeroSectionProps) => {
  const {
    state: { translate, langCode },
  } = useContext(I18nContext);

  return (
    <div style={{ ...cardStyle, padding: "24px 24px 28px" }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: "0 0 24px" }}>
        {translate("dashboard.menu.hello").replace("{name}", lead?.name || "")}
      </h1>
      <div
        className={`grid gap-4 ${isCancelled ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}
      >
        {/* Capital */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: 20,
            backgroundColor: "#eef2ff",
            borderRadius: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              backgroundColor: "#4f46e5",
              borderRadius: 12,
              flexShrink: 0,
            }}
          >
            <GlobalIcon iconName="ShieldLockIcon" size="XS" color="#fff" />
          </div>
          <div>
            <p style={{ fontSize: 13, fontWeight: 500, color: "#6366f1", margin: 0 }}>
              {translate("newDashboard.assuredCapital")}
            </p>
            <p style={{ fontSize: 28, fontWeight: 800, color: "#312e81", margin: 0 }}>
              {numberFormat(capital, "capital")}
            </p>
            <PendingCapitalLine pendingCapital={pendingCapital} current={capital} />
          </div>
        </div>
        {/* Next fee */}
        {!isCancelled && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: 20,
              backgroundColor: "#f9fafb",
              borderRadius: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                backgroundColor: "#e0e7ff",
                borderRadius: 12,
                flexShrink: 0,
              }}
            >
              <GlobalIcon iconName="CurrencyEuroIcon" size="XS" color="#4f46e5" />
            </div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 500, color: "#6b7280", margin: 0 }}>
                {translate(
                  frequency === "MONTHLY"
                    ? "newDashboard.monthlyFee"
                    : "newDashboard.yearlyFee"
                )}
              </p>
              {null === displayPrice ? (
                <p data-testid="price-updating" style={{ fontSize: 18, fontWeight: 600, color: "#6b7280", margin: "4px 0 0" }}>
                  {priceUpdatingText(translate, langCode)}
                </p>
              ) : hasPromo && null !== regularPrice ? (
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 16, color: "#9ca3af", textDecoration: "line-through" }}>
                    {numberFormat(regularPrice, "price")}
                  </span>
                  <span style={{ fontSize: 28, fontWeight: 800, color: "#312e81" }}>
                    {numberFormat(displayPrice, "price")}
                  </span>
                </div>
              ) : (
                <p style={{ fontSize: 28, fontWeight: 800, color: "#312e81", margin: 0 }}>
                  {numberFormat(displayPrice, "price")}
                </p>
              )}
              {pricing?.promotion && pricing?.showPromo && null !== displayPrice && (
                <button
                  onClick={onShowConditions}
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "#4f46e5",
                    fontSize: 13,
                    fontWeight: 500,
                    padding: 0,
                    marginTop: 4,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                >
                  {translate("newDashboard.promotion")}
                </button>
              )}
              {nextPaymentDate && (
                <p style={{ fontSize: 13, color: "#9ca3af", margin: "6px 0 0" }}>
                  {formatPaymentDate(nextPaymentDate)}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroSection;
